import React from 'react';

import type { DropResult } from './types';

type DroppableRegistry = Map<string, { isDropDisabled?: boolean }>;

export interface DndA11yContextValue {
  registerDroppable: (droppableId: string, isDropDisabled?: boolean) => void;
  unregisterDroppable: (droppableId: string) => void;

  onHandleFocus: (draggableId: string) => void;
  onHandleBlur: (draggableId: string) => void;

  onHandleKeyDown: (args: { draggableId: string; direction: -1 | 1; sourceDroppableId?: string }) => void;

  getDraggableClassName: (draggableId: string) => string;

  flashMoved: (draggableId: string) => void;
  flashKeyboardError: (draggableId: string) => void;
  focusHandle: (draggableId: string) => void;
}

export const DndA11yContext = React.createContext<DndA11yContextValue | null>(null);

export function useDndA11yContext() {
  return React.useContext(DndA11yContext);
}

export function DndA11yProvider(props: {
  children: React.ReactNode;
  droppableOrder?: string[];
  emitDragEnd?: (result: DropResult) => void;
}) {
  const emitDragEnd = props.emitDragEnd;
  const droppableOrder = React.useMemo(() => props.droppableOrder ?? [], [props.droppableOrder]);
  const registryRef = React.useRef<DroppableRegistry>(new Map());

  const [focusedId, setFocusedId] = React.useState<string | undefined>();
  const [movedId, setMovedId] = React.useState<string | undefined>();
  const [keyboardErrorId, setKeyboardErrorId] = React.useState<string | undefined>();

  const movedTimerRef = React.useRef<number | undefined>(undefined);
  const keyboardErrorTimerRef = React.useRef<number | undefined>(undefined);

  const registerDroppable = React.useCallback((droppableId: string, isDropDisabled?: boolean) => {
    registryRef.current.set(droppableId, { isDropDisabled });
  }, []);

  const unregisterDroppable = React.useCallback((droppableId: string) => {
    registryRef.current.delete(droppableId);
  }, []);

  const focusHandle = React.useCallback((draggableId: string) => {
    const elements = document.querySelectorAll('[data-eb-draggable-handle-id]');
    for (const element of Array.from(elements)) {
      if (element.getAttribute('data-eb-draggable-handle-id') === draggableId) {
        (element as HTMLElement).focus();
        return;
      }
    }
  }, []);

  const flashMoved = React.useCallback((draggableId: string) => {
    if (movedTimerRef.current) {
      window.clearTimeout(movedTimerRef.current);
    }
    setMovedId(draggableId);
    movedTimerRef.current = window.setTimeout(() => setMovedId(undefined), 550);
  }, []);

  const flashKeyboardError = React.useCallback((draggableId: string) => {
    if (keyboardErrorTimerRef.current) {
      window.clearTimeout(keyboardErrorTimerRef.current);
    }
    setKeyboardErrorId(draggableId);
    keyboardErrorTimerRef.current = window.setTimeout(() => setKeyboardErrorId(undefined), 550);
  }, []);

  const getDraggableClassName = React.useCallback(
    (draggableId: string) => {
      const classes = ['eb-dnd-draggable'];
      if (focusedId === draggableId) {
        classes.push('eb-dnd-draggable--focused');
      }
      if (keyboardErrorId === draggableId) {
        classes.push('eb-dnd-draggable--keyboard-error');
      } else if (movedId === draggableId) {
        classes.push('eb-dnd-draggable--moved');
      }
      return classes.join(' ');
    },
    [focusedId, movedId, keyboardErrorId]
  );

  const onHandleFocus = React.useCallback((draggableId: string) => {
    setFocusedId(draggableId);
  }, []);

  const onHandleBlur = React.useCallback(
    (draggableId: string) => {
      if (focusedId === draggableId) {
        setFocusedId(undefined);
      }
    },
    [focusedId]
  );

  const onHandleKeyDown = React.useCallback(
    (args: { draggableId: string; direction: -1 | 1; sourceDroppableId?: string }) => {
      if (!emitDragEnd) {
        flashKeyboardError(args.draggableId);
        return;
      }

      const sourceId = args.sourceDroppableId;
      if (!sourceId || !droppableOrder.length) {
        flashKeyboardError(args.draggableId);
        return;
      }

      const currentIndex = droppableOrder.indexOf(sourceId);
      if (currentIndex === -1) {
        flashKeyboardError(args.draggableId);
        return;
      }

      // Find next enabled droppable in the desired direction.
      let nextIndex = currentIndex + args.direction;
      while (nextIndex >= 0 && nextIndex < droppableOrder.length) {
        const candidateId = droppableOrder[nextIndex];
        const candidate = registryRef.current.get(candidateId);
        if (!candidate?.isDropDisabled) {
          // Emit the same shape as a drag end.
          emitDragEnd({
            draggableId: args.draggableId,
            source: { droppableId: sourceId, index: 0 },
            destination: { droppableId: candidateId, index: 0 },
          });

          flashMoved(args.draggableId);

          // Re-focus after the React tree has moved the element.
          requestAnimationFrame(() => requestAnimationFrame(() => focusHandle(args.draggableId)));
          return;
        }
        nextIndex += args.direction;
      }

      flashKeyboardError(args.draggableId);
    },
    [droppableOrder, flashKeyboardError, flashMoved, focusHandle, emitDragEnd]
  );

  const contextValue = React.useMemo<DndA11yContextValue>(
    () => ({
      registerDroppable,
      unregisterDroppable,
      onHandleFocus,
      onHandleBlur,
      onHandleKeyDown,
      getDraggableClassName,
      flashMoved,
      flashKeyboardError,
      focusHandle,
    }),
    [
      registerDroppable,
      unregisterDroppable,
      onHandleFocus,
      onHandleBlur,
      onHandleKeyDown,
      getDraggableClassName,
      flashMoved,
      flashKeyboardError,
      focusHandle,
    ]
  );

  return <DndA11yContext.Provider value={contextValue}>{props.children}</DndA11yContext.Provider>;
}
